"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildWhereCondition = buildWhereCondition;
exports.buildOrderCondition = buildOrderCondition;
exports.mapSortParams = mapSortParams;
exports.mapSearchParams = mapSearchParams;
exports.applySearchConditions = applySearchConditions;
exports.addDays = addDays;
exports.getDurationDates = getDurationDates;
const common_1 = require("@nestjs/common");
const constants_1 = require("../../../../../apps/services/service-lib/src/lib/constants");
const typeorm_1 = require("typeorm");
const constants_2 = require("../constants");
/**
 * Builds the where condition for the query.
 */
function buildWhereCondition(repository, where, searchArray, relations) {
    let whereCondition = where || {};
    if (searchArray && searchArray.length > 0) {
        const searchConditions = searchArray
            .filter(({ searchBy, searchValue }) => searchBy && searchValue)
            .reduce((acc, { searchBy, searchValue }) => {
            const isNestedProperty = searchBy.includes(".");
            const parsedSearchValue = parseSearchValue(searchValue);
            if (!parsedSearchValue)
                return acc;
            if (isNestedProperty) {
                handleNestedSearchCondition(acc, searchBy, parsedSearchValue, relations);
            }
            else {
                handleSimpleSearchCondition(acc, repository, searchBy, parsedSearchValue);
            }
            return acc;
        }, {});
        whereCondition = combineWhereConditions(whereCondition, searchConditions);
    }
    return whereCondition;
}
/**
 * Builds the order condition for the query.
 */
function buildOrderCondition(repository, sort, relations) {
    return sort.reduce((acc, { field, order }) => {
        const isNestedProperty = field.includes(".");
        if (isNestedProperty) {
            const [relation, column] = field.split(".");
            if (!relations)
                relations = {};
            if (!relations[relation])
                relations[relation] = true;
            acc[`${relation}.${column}`] = order;
        }
        else {
            const isValidColumn = repository.metadata.columns.some((col) => col.propertyName === field);
            if (isValidColumn) {
                acc[field] = order;
            }
            else {
                console.warn(`Invalid sort field: ${field}`);
            }
        }
        return acc;
    }, {});
}
/**
 * Parses the search value to handle arrays or strings.
 */
function parseSearchValue(searchValue) {
    if (searchValue.startsWith("[") && searchValue.endsWith("]")) {
        return searchValue
            .slice(1, -1)
            .split(",")
            .map((val) => val.trim());
    }
    return searchValue.trim() || undefined;
}
/**
 * Handles nested search conditions.
 */
function handleNestedSearchCondition(acc, searchBy, parsedSearchValue, relations) {
    const [relation, field] = searchBy.split(".");
    if (!relations)
        relations = {};
    if (!relations[relation])
        relations[relation] = true;
    if (!acc[relation])
        acc[relation] = {};
    acc[relation][field] = Array.isArray(parsedSearchValue)
        ? (0, typeorm_1.In)(parsedSearchValue)
        : (0, typeorm_1.ILike)(`%${parsedSearchValue}%`);
}
/**
 * Handles simple search conditions.
 */
function handleSimpleSearchCondition(acc, repository, searchBy, parsedSearchValue) {
    const columnType = repository.metadata.columns.find((col) => col.propertyName === searchBy)?.type;
    if (["varchar", "text"].includes(columnType)) {
        acc[searchBy] = Array.isArray(parsedSearchValue)
            ? (0, typeorm_1.In)(parsedSearchValue)
            : (0, typeorm_1.ILike)(`%${parsedSearchValue}%`);
    }
    else if (["number", "integer", "float"].includes(columnType)) {
        acc[searchBy] = Number(parsedSearchValue);
    }
    else if (["date", "timestamp"].includes(columnType)) {
        acc[searchBy] = (0, typeorm_1.Between)(new Date(Array.isArray(parsedSearchValue)
            ? parsedSearchValue[0]
            : parsedSearchValue), new Date(Array.isArray(parsedSearchValue)
            ? parsedSearchValue[1]
            : parsedSearchValue));
    }
}
/**
 * Combines existing where conditions with new search conditions.
 */
function combineWhereConditions(whereCondition, searchConditions) {
    if (Array.isArray(whereCondition)) {
        return whereCondition.map((condition) => ({
            ...condition,
            ...searchConditions,
        }));
    }
    else if (Object.keys(whereCondition).length > 0) {
        return {
            ...whereCondition,
            ...searchConditions,
        };
    }
    else {
        return searchConditions;
    }
}
/**
 * Maps sort parameters to the format required by TypeORM.
 */
function mapSortParams(params) {
    console.log("params", params);
    if (!params)
        return [];
    const sortArray = params
        .split(",")
        .map((item) => {
        const [rawKey, rawOrder] = item.trim().split(":");
        const key = rawKey.trim();
        const order = (rawOrder || "ASC").trim().toUpperCase();
        const field = constants_1.ATTRIBUTE_FIELD_MAP[key]; // Default to "created_at" if key is not mapped
        return { field, order };
    })
        .filter(({ field }) => Boolean(field));
    console.log("sortArray", sortArray);
    return sortArray;
}
/**
 * Splits the params string on top-level commas (ignoring commas inside brackets).
 */
function splitParams(params) {
    const result = [];
    let current = "";
    let depth = 0;
    for (const char of params) {
        if (char === "[")
            depth++;
        if (char === "]")
            depth--;
        if (char === "," && depth === 0) {
            result.push(current);
            current = "";
        }
        else {
            current += char;
        }
    }
    if (current.trim())
        result.push(current);
    return result;
}
/**
 * Parses a search param string into the array format used by the query builder.
 * @param params Comma-separated "key:value" or "key:[v1,v2]" entries,
 *   where value can be a string, number, date (ISO), or array of those.
 */
function mapSearchParams(params) {
    if (!params)
        return [];
    return splitParams(params)
        .map((item) => {
        const idx = item.indexOf(":");
        if (idx < 0)
            return null;
        const rawKey = item.slice(0, idx).trim();
        const rawVal = item.slice(idx + 1).trim();
        let searchValue;
        if (rawVal.startsWith("[") && rawVal.endsWith("]")) {
            // array of values inside brackets
            const inner = rawVal.slice(1, -1);
            searchValue = splitParams(inner)
                .map((v) => v.trim())
                .filter((v) => v)
                .map((v) => parseValue(v));
        }
        else {
            // single value
            searchValue = parseValue(rawVal);
        }
        const searchBy = constants_1.ATTRIBUTE_FIELD_MAP[rawKey] ?? rawKey;
        return { searchBy, searchValue };
    })
        .filter((item) => Boolean(item));
}
/**
 * Attempts to parse a string into number or Date; falls back to string.
 */
function parseValue(v) {
    const num = Number(v);
    if (!isNaN(num) && v !== "") {
        return num;
    }
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
        return d;
    }
    return v;
}
function applySearchConditions(qb, search, searchFields) {
    if (!search?.trim() ||
        !Array.isArray(searchFields) ||
        searchFields.length === 0) {
        return;
    }
    console.log("searchFields", searchFields);
    const searchTerms = search.trim().split(/\s+/);
    const conditions = [];
    // Check if all contact name fields are present
    const hasFullNameFields = searchFields.includes("firstName") &&
        searchFields.includes("middleName") &&
        searchFields.includes("lastName");
    // Apply enhanced full name search logic only when all three name fields are present (contact name search)
    if (hasFullNameFields) {
        // Handle full name search with 1, 2, or 3 words
        if (searchTerms.length === 2) {
            const [first, second] = searchTerms;
            // Match: first + middle OR first + last
            qb.setParameter("firstName_twoWords", `%${first}%`);
            qb.setParameter("middleOrLastName_twoWords", `%${second}%`);
            conditions.push(`(
          main.first_name ILIKE :firstName_twoWords AND (
          main.middle_name ILIKE :middleOrLastName_twoWords OR
          main.last_name ILIKE :middleOrLastName_twoWords
        )
      )`);
            // Optional: reverse order
            qb.setParameter("lastName_twoWords_alt", `%${first}%`);
            qb.setParameter("firstName_twoWords_alt", `%${second}%`);
            conditions.push(`(
          main.last_name ILIKE :lastName_twoWords_alt AND main.first_name ILIKE :firstName_twoWords_alt
      )`);
        }
        else if (searchTerms.length === 3) {
            const [first, middle, last] = searchTerms;
            qb.setParameter("firstName_threeWords", `%${first}%`);
            qb.setParameter("middleName_threeWords", `%${middle}%`);
            qb.setParameter("lastName_threeWords", `%${last}%`);
            conditions.push(`(
          main.first_name ILIKE :firstName_threeWords AND 
          main.middle_name ILIKE :middleName_threeWords AND 
          main.last_name ILIKE :lastName_threeWords
      )`);
        }
    }
    const searchConditions = searchFields.map((field, index) => {
        let alias, column;
        if (field.includes(".")) {
            const parts = field.split(".");
            column = parts.pop();
            alias = parts.join("_");
        }
        else {
            alias = "main";
            column = field;
        }
        const paramKey = `searchParam_${index}`;
        qb.setParameter(paramKey, `%${search}%`);
        return `${alias}.${column} ILIKE :${paramKey}`;
    });
    // Combine all conditions
    const allConditions = [...conditions, ...searchConditions];
    if (allConditions.length > 0) {
        qb.andWhere(`(${allConditions.join(" OR ")})`);
    }
}
function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}
function getDurationDates(durationKey) {
    const endDate = new Date();
    const startDate = new Date();
    switch (durationKey) {
        case constants_2.THREE_MONTHS:
            startDate.setMonth(endDate.getMonth() - 3);
            break;
        case constants_2.SIX_MONTHS:
            startDate.setMonth(endDate.getMonth() - 6);
            break;
        case constants_2.ONE_YEAR:
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
        case constants_2.TWO_YEARS:
            startDate.setFullYear(endDate.getFullYear() - 2);
            break;
        case constants_2.FIVE_YEARS:
            startDate.setFullYear(endDate.getFullYear() - 5);
            break;
        default:
            throw new common_1.BadRequestException("Invalid duration key");
    }
    return { startDate, endDate };
}
